import En from '@/common/classes/En'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'

type EnInit = ConstructorParameters<typeof En>[0]

export function normalizeConditionResult(result: unknown): boolean {
  if (result === null || result === undefined) {
    return false
  }
  if (typeof result === 'string') {
    const normalized = String(result).trim().toLowerCase()
    if (
      normalized === '' ||
      normalized === 'false' ||
      normalized === 'null' ||
      normalized === 'undefined' ||
      normalized === '0'
    ) {
      return false
    }
    if (normalized === 'true') {
      return true
    }
  }
  return Boolean(result)
}

/**
 * Параметры вычисления условия видимости из разметки (`condition` у panel/element).
 */
export type EvaluateLayoutConditionParams = {
  condition: string
  formProvider: {
    getForm: (opts: { formId: string | number; panelId?: string | number }) => Record<string, unknown>
    getElements?: EnInit['getElements']
    getActions?: () => EnInit['actions']
  } | null
  pageData: unknown
  formId: string | number
  panelId?: string | number
  elementId?: string | number
  measureLabel?: string
}

export async function evaluateLayoutCondition(
  params: EvaluateLayoutConditionParams,
): Promise<boolean> {
  const rawCondition = String(params.condition ?? '').trim()
  if (!rawCondition) {
    return true
  }

  const formProvider = params.formProvider
  if (!formProvider) {
    return false
  }

  const endMeasure = startWorkflowMeasure(params.measureLabel ?? 'layout.condition.evaluate', {
    formId: params.formId,
    panelId: params.panelId,
    elementId: params.elementId,
  })

  try {
    const getElements = formProvider.getElements
    const getActions = formProvider.getActions
    const en = new En({
      controls: formProvider,
      elements: getElements ? getElements() : {},
      getElements,
      actions: getActions ? getActions() : {},
      pageData: params.pageData,
      formId: Number(params.formId),
      panelId: params.panelId != null ? Number(params.panelId) : undefined,
      elementId: params.elementId != null ? Number(params.elementId) : undefined,
    })

    const fullFormData = formProvider.getForm({ formId: params.formId })
    const scopedFormData =
      params.panelId != null
        ? formProvider.getForm({ formId: params.formId, panelId: params.panelId })
        : {}

    en._resetData(
      fullFormData && Object.keys(fullFormData).length > 0 ? fullFormData : scopedFormData,
    )

    let result
    try {
      // Wrap condition in return to get the expression result
      const wrappedCondition = `return (${rawCondition})`
      result = await en.run(wrappedCondition)
    } catch (error) {
      throw error
    }

    return normalizeConditionResult(result)
  } catch (error) {
    console.error('Error evaluating layout condition:', error)
    return false
  } finally {
    endMeasure()
  }
}
